import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { X, Users } from 'lucide-react';
import { avatarTint, initials } from './inbox/avatars';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** The address's domain, lowercased, or '' when there isn't one. */
function domainOf(email) {
    const at = String(email || '').lastIndexOf('@');
    return at === -1 ? '' : String(email).slice(at + 1).toLowerCase();
}

/**
 * ContactEmailTypeahead — Search CRM contacts by name/email with tag-style multi-select.
 *
 * Props: value (array of {email, name?}), onChange, placeholder, allowFreeText (type any email)
 *
 * @param {'boxed'|'bare'} [variant] - `boxed` (the default) is the bordered
 *   field every existing call site draws. `bare` drops the border, the ring and
 *   the padding so the control can sit inside a composer's hairline row, where
 *   the ROW is the field and a second box inside it reads as a nested input.
 * @param {boolean} [suggestOnFocus] - fetch a short contact list when the input
 *   is focused with an empty query, which is what makes the design's "Suggested
 *   contacts" menu appear before anything is typed. Off by default: every other
 *   call site would otherwise fire a request on every focus.
 * @param {string} [ownDomain] - the sending mailbox's domain. Supplying it turns
 *   on the `External` chip for suggestions outside it. Derived here from two
 *   addresses the caller already has, exactly as `PersonalThreadView` derives
 *   the reader's chip — there is no external/internal flag on a contact, and a
 *   missing domain means no chip rather than a guess.
 */
export default function ContactEmailTypeahead({
    value = [],
    onChange,
    placeholder = 'Search contacts...',
    allowFreeText = true,
    variant = 'boxed',
    suggestOnFocus = false,
    ownDomain = '',
}) {
    const { getAuthHeader } = useAuth();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    const bare = variant === 'bare';

    useEffect(() => {
        const trimmed = query.trim();
        // An empty query is a real search when the caller asked for
        // suggestions-on-focus: `?page_size=8` with no `search` is the contact
        // list's own first page, which is what the design's menu shows.
        const wantsSuggestions = suggestOnFocus && focused && trimmed.length === 0;
        if (trimmed.length < 2 && !wantsSuggestions) { setResults([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const url = trimmed.length >= 2
                    ? `${API}/contacts?search=${encodeURIComponent(trimmed)}&page_size=8`
                    : `${API}/contacts?page_size=8`;
                const res = await axios.get(url, { headers: getAuthHeader() });
                const contacts = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setResults(contacts.filter(c => c.email && !value.find(v => v.email === c.email)));
                setShowDropdown(true);
            } catch {}
            finally { setLoading(false); }
        }, wantsSuggestions ? 0 : 300);
        return () => clearTimeout(debounceRef.current);
    }, [query, focused, suggestOnFocus, getAuthHeader, value]);

    const addContact = (contact) => {
        onChange([...value, { email: contact.email, name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(), contact_id: contact.contact_id }]);
        setQuery('');
        setResults([]);
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    const addFreeEmail = () => {
        const email = query.trim();
        if (email && email.includes('@') && !value.find(v => v.email === email)) {
            onChange([...value, { email, name: '' }]);
            setQuery('');
        }
    };

    const remove = (email) => {
        onChange(value.filter(v => v.email !== email));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (results.length > 0) { addContact(results[0]); }
            else if (allowFreeText && query.includes('@')) { addFreeEmail(); }
        }
        if (e.key === 'Backspace' && !query && value.length > 0) {
            remove(value[value.length - 1].email);
        }
    };

    // Accepts either a bare domain or a whole address, so a caller can hand
    // over the sending mailbox without pre-splitting it.
    const own = String(ownDomain || '').includes('@')
        ? domainOf(ownDomain)
        : String(ownDomain || '').toLowerCase();

    return (
        <div className={bare ? 'relative min-w-0 flex-1' : 'relative'}>
            <div className={bare
                ? 'flex flex-wrap items-center gap-1.5 min-w-0'
                : 'flex flex-wrap gap-1 p-1.5 border border-stone-300 rounded-lg bg-white min-h-[38px] focus-within:ring-2 focus-within:ring-stone-900/30 focus-within:border-stone-400'}
            >
                {value.map(v => (bare ? (
                    <span
                        key={v.email}
                        className="inline-flex items-center gap-[7px] h-[26px] px-1 bg-[#F4F2EE] rounded-full"
                    >
                        <span className={`w-[18px] h-[18px] rounded-full text-[9px] font-semibold grid place-items-center ${avatarTint(v.name || v.email)}`}>
                            {initials(v.name || v.email, 1)}
                        </span>
                        <span className="text-[12.5px] text-[#0C0A09] whitespace-nowrap">{v.name || v.email}</span>
                        <button
                            type="button"
                            onClick={() => remove(v.email)}
                            aria-label={`Remove ${v.email}`}
                            className="w-[18px] h-[18px] rounded-full grid place-items-center text-[#57504B] hover:bg-[#E4E1DB]"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ) : (
                    <span key={v.email} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full">
                        {v.name || v.email}
                        <button onClick={() => remove(v.email)} className="text-stone-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                )))}
                <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setFocused(true);
                        if (query.length >= 2 || (suggestOnFocus && results.length)) setShowDropdown(true);
                    }}
                    onBlur={() => {
                        setFocused(false);
                        // Commit typed-but-unsubmitted email on blur so users who
                        // type an address and click Send (without pressing Enter)
                        // don't get a "no recipient" error.
                        if (allowFreeText && query.trim().includes('@')) {
                            addFreeEmail();
                        }
                        setTimeout(() => setShowDropdown(false), 200);
                    }}
                    placeholder={value.length === 0 ? placeholder : ''}
                    className={bare
                        ? 'flex-1 min-w-[160px] text-[13.5px] text-[#0C0A09] placeholder:text-[#A29B95] border-none outline-none bg-transparent py-1'
                        : 'flex-1 min-w-[120px] text-sm border-none outline-none bg-transparent px-1 py-0.5'} />
            </div>

            {showDropdown && results.length > 0 && (bare ? (
                <div className="absolute z-30 left-0 top-full mt-1 w-[340px] max-w-full bg-white
                                border border-[#E9E7E2] rounded-[11px] shadow-[0_14px_36px_rgba(12,10,9,.14)]
                                p-1.5 max-h-72 overflow-y-auto"
                     data-testid="contact-suggestions">
                    <div className="px-2.5 pt-2 pb-1.5 text-[11px] font-semibold text-[#57504B]">
                        Suggested contacts
                    </div>
                    {results.map(c => {
                        const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;
                        const external = Boolean(own) && Boolean(domainOf(c.email)) && domainOf(c.email) !== own;
                        return (
                            <button
                                key={c.contact_id}
                                type="button"
                                onMouseDown={() => addContact(c)}
                                className="w-full flex items-center gap-[11px] px-2.5 py-2 rounded-lg text-left hover:bg-[#F6F5F1]"
                            >
                                <span className={`w-7 h-7 rounded-full text-[11px] font-semibold grid place-items-center flex-shrink-0 ${avatarTint(name)}`}>
                                    {initials(name, 2)}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-[7px]">
                                        <span className="text-[13px] text-[#0C0A09] truncate">{name}</span>
                                        {external && (
                                            <span className="text-[10px] text-[#57504B] bg-[#F1EFEA] rounded px-[5px] py-px flex-shrink-0">
                                                External
                                            </span>
                                        )}
                                    </span>
                                    <span className="block text-[11.5px] text-[#A29B95] truncate">{c.email}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {results.map(c => (
                        <button key={c.contact_id} onMouseDown={() => addContact(c)}
                            className="w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2">
                            <Users className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-xs font-medium text-stone-700 truncate">
                                    {c.first_name} {c.last_name}
                                </div>
                                <div className="text-[10px] text-stone-400 truncate">{c.email}</div>
                            </div>
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
}
